import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
} from "@mui/material";
import CardLoader from "../../components/CardLoader";
import { useCommon } from "../../contexts/common";
import { emailTypes } from "../../constants/lists";

const UnsubscribeEmail = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [unsubscribingAll, setUnsubscribingAll] = useState(false);
  const [step, setStep] = useState("initial"); // 'initial', 'confirm', 'success', 'error'
  const [emailType, setEmailType] = useState("");
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(5);

  const userId = searchParams.get("user_id");
  const emailTypeParam = searchParams.get("email_type");
  const {
    verifyEmailUser,
    unsubscribeEmailNotification,
    unsubscribeAllEmailNotification,
  } = useCommon();
  const navigate = useNavigate();

  useEffect(() => {
    if (userId && emailTypeParam) {
      validateUnsubscribe();
    } else {
      setError("Invalid unsubscribe link");
      setStep("error");
      setLoading(false);
    }
  }, [userId, emailTypeParam]);

  const validateUnsubscribe = async () => {
    try {
      const response = await verifyEmailUser({
        user_id: userId,
        email_type: emailTypeParam,
      });
      if (response.status === 200) {
        if (response.data.valid) {
          const emailTypeValue = response.data.email_type || emailTypeParam;
          const emailTypeObj = emailTypes.find(
            (type) => type.value === emailTypeValue
          );
          setEmailType(emailTypeObj?.label || emailTypeValue);
          setStep("confirm");
        } else {
          setError("User not found");
          setStep("error");
        }
      }
    } catch (err) {
      setError("Invalid unsubscribe request");
      setStep("error");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmUnsubscribe = async () => {
    setConfirming(true);
    try {
      const response = await unsubscribeEmailNotification({
        user_id: userId,
        email_type: emailTypeParam,
      });

      if (response.status === 200) {
        setTimeout(() => {
          setStep("success");
          setConfirming(false);
          const timer = setInterval(() => {
            setCountdown((prev) => {
              if (prev <= 1) {
                clearInterval(timer);
                navigate("/");
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        }, 2000);
      }
    } catch (err) {
      setError("Failed to unsubscribe. Please try again.");
      setStep("error");
      setConfirming(false);
    }
  };

  const handleUnsubscribeAll = async () => {
    setUnsubscribingAll(true);
    try {
      const response = await unsubscribeAllEmailNotification({
        user_id: userId,
      });

      if (response.status === 200) {
        setTimeout(() => {
          setStep("success");
          setEmailType("all");
          setUnsubscribingAll(false);
          const timer = setInterval(() => {
            setCountdown((prev) => {
              if (prev <= 1) {
                clearInterval(timer);
                navigate("/");
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        }, 2000);
      }
    } catch (err) {
      setError("Failed to unsubscribe from all emails. Please try again.");
      setStep("error");
      setUnsubscribingAll(false);
    }
  };

  const handleCancel = () => {
    navigate("/");
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CardLoader />
      </Box>
    );
  }

  return (
    <Box maxWidth="600px" mx="auto" p={3}>
      <Card>
        <CardContent sx={{ p: 4, textAlign: "center" }}>
          {step === "confirm" && (
            <>
              <Typography
                variant="h4"
                gutterBottom
                sx={{ fontWeight: "bold", mb: 2 }}
              >
                Unsubscribe from Email
              </Typography>
              <Typography
                variant="body1"
                color="text.primary"
                sx={{ mb: 2, fontSize: "1.1rem" }}
              >
                You are about to unsubscribe from <strong>{emailType}</strong>{" "}
                emails.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Are you sure you want to continue?
              </Typography>
              <Box
                display="flex"
                flexDirection="column"
                gap={5}
                alignItems="center"
              >
                <Box display="flex" gap={5}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleConfirmUnsubscribe}
                    disabled={confirming || unsubscribingAll}
                  >
                    {confirming ? "Unsubscribing..." : "Confirm"}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    disabled={confirming || unsubscribingAll}
                  >
                    Cancel
                  </Button>
                </Box>
                <Box display="flex" flexDirection={"column"} gap={0.5}>
                  <Button
                    variant="text"
                    color="primary"
                    onClick={() => navigate("/settings/Notifications")}
                    disabled={confirming || unsubscribingAll}
                    sx={{ textDecoration: "underline", py: 0, fontSize: "16px" }}
                  >
                    Manage Email Preferences
                  </Button>
                  <Button
                    variant="text1"
                    color="primary"
                    onClick={handleUnsubscribeAll}
                    disabled={confirming || unsubscribingAll}
                    sx={{ textDecoration: "underline", py: 0 }}
                  >
                    {unsubscribingAll
                      ? "Unsubscribing from all..."
                      : "Unsubscribe from all emails"}
                  </Button>
                </Box>
              </Box>
            </>
          )}

          {step === "success" && (
            <>
              <Typography
                variant="h4"
                gutterBottom
                color="success.main"
                sx={{ fontWeight: "bold", mb: 2 }}
              >
                Successfully Unsubscribed
              </Typography>
              <Typography
                variant="body1"
                color="text.primary"
                sx={{ mb: 3, fontSize: "1.1rem" }}
              >
                You have been unsubscribed from{" "}
                <strong>{emailType === "all" ? "all" : emailType}</strong>{" "}
                emails.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                You can resubscribe anytime by visiting your Notifications
                Settings page.
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: "italic" }}
              >
                Redirecting you to the homepage in {countdown} seconds...
              </Typography>
            </>
          )}

          {step === "error" && (
            <>
              <Typography variant="h5" gutterBottom color="error.main">
                Error
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {error}
              </Typography>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default UnsubscribeEmail;
